import React from 'react';
import {
  ShieldCheck,
  Lock,
  UserCheck,
  AlertTriangle,
  KeyRound,
  MessageCircleWarning,
} from 'lucide-react';

const items = [
  {
    icon: Lock,
    title: 'Conexiones protegidas',
    text: 'PetAdvisor utiliza conexiones HTTPS para proteger la información transmitida entre tu dispositivo y la plataforma.',
  },
  {
    icon: KeyRound,
    title: 'Protección de credenciales',
    text: 'Las contraseñas no deben almacenarse en texto visible. Utilizamos mecanismos de autenticación y controles de acceso para proteger las cuentas.',
  },
  {
    icon: UserCheck,
    title: 'Acceso restringido',
    text: 'El acceso a funciones administrativas y datos internos está limitado según el rol y las necesidades de cada usuario autorizado.',
  },
  {
    icon: ShieldCheck,
    title: 'Protección de datos',
    text: 'Aplicamos medidas técnicas y organizativas razonables conforme al RGPD y a la normativa española de protección de datos.',
  },
  {
    icon: AlertTriangle,
    title: 'Prevención y supervisión',
    text: 'Podemos registrar actividad técnica, limitar accesos y revisar comportamientos anómalos para prevenir fraude, abuso o accesos no autorizados.',
  },
  {
    icon: MessageCircleWarning,
    title: 'Comunicación de incidencias',
    text: 'Los usuarios pueden comunicar vulnerabilidades, accesos sospechosos o contenidos problemáticos mediante nuestros canales de contacto.',
  },
];

export default function Seguridad() {
  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="seguridad-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Seguridad en PetAdvisor
          </h1>
          <p className="mt-4 text-gray-600 max-w-3xl mx-auto leading-7">
            Trabajamos para proteger la plataforma, las cuentas y los datos
            personales mediante medidas técnicas, organizativas y buenas
            prácticas de seguridad.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {items.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="bg-white border border-gray-200 rounded-2xl p-6"
            >
              <div className="w-11 h-11 rounded-full bg-[#ffff00] flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-black" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              <p className="mt-2 text-gray-600 leading-7">{text}</p>
            </div>
          ))}
        </div>

        <section className="mt-10 bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-900">
            Cómo puedes proteger tu cuenta
          </h2>

          <ul className="mt-4 list-disc pl-6 text-gray-700 leading-7 space-y-2">
            <li>Utiliza una contraseña larga y exclusiva para PetAdvisor.</li>
            <li>No compartas tu contraseña ni códigos de acceso.</li>
            <li>Cierra sesión cuando utilices dispositivos compartidos.</li>
            <li>
              Verifica el dominio petadvisor.es antes de introducir tus datos.
            </li>
            <li>
              Desconfía de mensajes que soliciten contraseñas o datos completos
              de tarjetas.
            </li>
            <li>
              Mantén actualizado tu navegador, sistema operativo y dispositivo.
            </li>
          </ul>
        </section>

        <section className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-900">
            Contratación segura de proveedores
          </h2>

          <p className="mt-3 text-gray-700 leading-7">
            PetAdvisor facilita información y contacto con proveedores
            independientes, pero no sustituye las comprobaciones que debe hacer
            cada usuario antes de contratar.
          </p>

          <ul className="mt-4 list-disc pl-6 text-gray-700 leading-7 space-y-2">
            <li>Confirma la identidad y los datos del proveedor.</li>
            <li>Solicita precios, condiciones y políticas de cancelación.</li>
            <li>Consulta instalaciones y autorizaciones cuando correspondan.</li>
            <li>No realices pagos mediante canales que te generen dudas.</li>
            <li>
              Conserva comprobantes, conversaciones y condiciones acordadas.
            </li>
          </ul>
        </section>

        <section className="mt-6 bg-black text-white rounded-2xl p-6 md:p-8">
          <h2 className="text-2xl font-bold">
            ¿Has detectado una incidencia?
          </h2>
          <p className="mt-3 text-gray-300 leading-7">
            Comunica accesos sospechosos, posibles vulnerabilidades o problemas
            de seguridad a{' '}
            <a
              href="mailto:hola@petadvisor.es"
              className="text-[#ffff00] font-semibold underline"
            >
              hola@petadvisor.es
            </a>
            . No incluyas contraseñas, datos completos de tarjetas ni otra
            información especialmente sensible.
          </p>
        </section>
      </div>
    </div>
  );
}
